/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ChainedCommands, Editor } from "@tiptap/react";
import { motion } from "framer-motion";
import { nanoid } from "nanoid";
import React, { useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { BsCheckCircleFill } from "react-icons/bs";
import {
  FaAlignCenter,
  FaAlignJustify,
  FaAlignLeft,
  FaAlignRight,
  FaBold,
  FaHeading,
  FaItalic,
  FaLink,
  FaUnderline,
} from "react-icons/fa";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { Instance } from "tippy.js";
import { ShowMergeMenuAtom } from "../../../../atoms/ShowMergeMenuAtom";
import { useBrowserAgent } from "../../../../utils/BrowserAgent";
import {
  getSelectedAnchorElement,
  getSelectedEditorText,
  getSelectedRange,
} from "../../../../utils/LinkUtils";
import { ModalAtom } from "../../../Modal/Modal";
import { BubbleMenu } from "../../../NewEmail/BubbleMenu/BubbleMenu";
import useLinkPopover from "./useNoteLinkPopover";

interface Props {
  editor: Editor;
  tippyInstance: Instance | null;
  tooltips?: React.MutableRefObject<{ [href: string]: Instance }>;
}

const TiptapMenuAtom = atom({
  key: "NoteTiptapMenuAtom",
  default: nanoid(),
});

// Function used to update the Floating Menu as it doesn't update itself
// on bold and italic changes
export function useUpdateBubbleMenu() {
  let setTiptapMenuAtom = useSetRecoilState(TiptapMenuAtom);

  function updateMenuAtom() {
    setTiptapMenuAtom(nanoid());
  }

  return {
    updateMenuAtom,
  };
}

function editorChainKeepAlign(editor: Editor, chain: ChainedCommands) {
  // Heading resets alignment, so I need to do that too
  if (editor.isActive({ textAlign: "left" })) chain.setTextAlign("left");
  else if (editor.isActive({ textAlign: "right" })) chain.setTextAlign("right");
  else if (editor.isActive({ textAlign: "center" }))
    chain.setTextAlign("center");
}

function BubbleButtons(props: Props) {
  const { editor, tooltips } = props;
  // Should have
  let _ = useRecoilState(TiptapMenuAtom);
  let { updateMenuAtom } = useUpdateBubbleMenu();
  const { openLinkModal } = useLinkPopover(editor, tooltips);
  const { isWebKit } = useBrowserAgent();

  function cleanWebKitTooltips(tooltipId?: string, decorate?: boolean) {
    if (!isWebKit) return;
    Object.values(tooltips.current).forEach((t) => t.destroy());
    tooltips.current = {};
    // Leaving this commented to avoid losing all types the selection (when it's not a link selection)
    // applying a mark.
    // getAnchorByURLAndSelect(tooltipId, decorate);
  }

  const currentHeading = useMemo(() => {
    let noHeading =
      !editor.isActive("heading", { level: 1 }) &&
      !editor.isActive("heading", { level: 2 }) &&
      !editor.isActive("heading", { level: 3 });

    if (editor.isActive("heading", { level: 1 }))
      return (
        <button
          onClick={() => {
            // Toggle to the next one
            let chain = editor.chain().focus().toggleHeading({ level: 2 });
            editorChainKeepAlign(editor, chain);
            chain.run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={
            editor.isActive("heading", { level: 1 }) ? "is-active" : ""
          }
        >
          <FaHeading />1
        </button>
      );

    if (editor.isActive("heading", { level: 2 }))
      return (
        <button
          onClick={() => {
            let chain = editor.chain().focus().toggleHeading({ level: 3 });
            editorChainKeepAlign(editor, chain);
            chain.run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={
            editor.isActive("heading", { level: 2 }) ? "is-active" : ""
          }
        >
          <FaHeading />2
        </button>
      );

    if (editor.isActive("heading", { level: 3 }))
      return (
        <button
          onClick={() => {
            let chain = editor.chain().focus().toggleHeading({ level: 3 });
            editorChainKeepAlign(editor, chain);
            chain.run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={
            editor.isActive("heading", { level: 3 }) ? "is-active" : ""
          }
        >
          <FaHeading />3
        </button>
      );

    // Toggle the third one back to paragraph
    if (noHeading)
      return (
        <button
          onClick={() => {
            let chain = editor.chain().focus().toggleHeading({ level: 1 });
            editorChainKeepAlign(editor, chain);
            chain.run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={noHeading ? "is-active" : ""}
        >
          <FaHeading />
        </button>
      );
  }, [editor, updateMenuAtom]);

  const currentAlignment = useMemo(() => {
    if (editor.isActive({ textAlign: "left" }))
      return (
        <button
          // Toggle to the next
          onClick={() => {
            editor.chain().focus().setTextAlign("center").run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={editor.isActive({ textAlign: "left" }) ? "is-active" : ""}
        >
          <FaAlignLeft />
        </button>
      );

    if (editor.isActive({ textAlign: "center" }))
      return (
        <button
          onClick={() => {
            editor.chain().focus().setTextAlign("right").run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={
            editor.isActive({ textAlign: "center" }) ? "is-active" : ""
          }
        >
          <FaAlignCenter />
        </button>
      );

    if (editor.isActive({ textAlign: "right" }))
      return (
        <button
          onClick={() => {
            editor.chain().focus().setTextAlign("justify").run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={editor.isActive({ textAlign: "right" }) ? "is-active" : ""}
        >
          <FaAlignRight />
        </button>
      );

    if (editor.isActive({ textAlign: "justify" }))
      return (
        <button
          onClick={() => {
            editor.chain().focus().setTextAlign("left").run();
            cleanWebKitTooltips();
            updateMenuAtom();
          }}
          className={
            editor.isActive({ textAlign: "justify" }) ? "is-active" : ""
          }
        >
          <FaAlignJustify />
        </button>
      );

    return (
      <button
        // Toggle to the next
        onClick={() => {
          editor.chain().focus().setTextAlign("center").run();
          cleanWebKitTooltips();
          updateMenuAtom();
        }}
        className={editor.isActive({ textAlign: "left" }) ? "is-active" : ""}
      >
        <FaAlignLeft />
      </button>
    );
  }, [editor, updateMenuAtom]);

  return (
    <div
      css={css`
        border-radius: 4px;
        display: flex;
        flex-direction: row;
        padding: 0 3px;
        height: 36px;
        align-items: center;
        justify-content: center;
        background-color: #fb972e;
        box-shadow: 0px 3px 15px #00000029;
      `}
    >
      <motion.button
        layoutId="Button#1"
        onClick={() => {
          const selectedAnchor: HTMLAnchorElement | null =
            getSelectedAnchorElement();

          if (selectedAnchor) {
            const boldByStyle =
              selectedAnchor.style.fontWeight &&
              /^(bold(er)?|[5-9]\d{2,})$/.test(
                selectedAnchor.style.fontWeight as string
              );

            let boldByDecoration: any = selectedAnchor.parentNode;
            let isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent

            if (!isBoldByDecoration) {
              boldByDecoration = boldByDecoration.parentNode;
              isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
            }

            if (!isBoldByDecoration) {
              boldByDecoration = boldByDecoration.parentNode;
              isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
            }

            if (isBoldByDecoration && boldByStyle) {
              selectedAnchor.style.fontWeight = "none !important";
              editor.chain().focus().unsetBold().run();
            } else if (isBoldByDecoration) {
              editor.chain().focus().unsetBold().run();
            } else if (boldByStyle) {
              selectedAnchor.style.fontWeight = "none !important";
              editor.chain().focus().unsetBold().run();
            } else {
              editor.chain().focus().toggleBold().run();
              cleanWebKitTooltips(
                selectedAnchor.getAttribute("data-tooltip-id"),
                true
              );
              return;
            }
            cleanWebKitTooltips(selectedAnchor.getAttribute("data-tooltip-id"));
          } else {
            editor.chain().focus().toggleBold().run();
          }
        }}
        className={editor.isActive("bold") ? "is-active" : ""}
      >
        <FaBold />
      </motion.button>
      <button
        onClick={() => {
          const selectedAnchor: HTMLAnchorElement | null =
            getSelectedAnchorElement();

          if (selectedAnchor) {
            const italicByStyle = selectedAnchor.style.fontStyle === "italic";

            let italicByDecoration: any = selectedAnchor.parentNode;
            let isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent

            if (!isItalicByDecoration) {
              italicByDecoration = italicByDecoration.parentNode;
              isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent
            }

            if (isItalicByDecoration && italicByStyle) {
              selectedAnchor.style.fontStyle = "none !important";
              editor.chain().focus().unsetItalic().run();
            } else if (isItalicByDecoration) {
              editor.chain().focus().unsetItalic().run();
            } else if (italicByStyle) {
              selectedAnchor.style.fontStyle = "none !important";
              editor.chain().focus().unsetItalic().run();
            } else {
              editor.chain().focus().toggleItalic().run();
              cleanWebKitTooltips(
                selectedAnchor.getAttribute("data-tooltip-id"),
                true
              );
              return;
            }
            cleanWebKitTooltips(selectedAnchor.getAttribute("data-tooltip-id"));
          } else {
            editor.chain().focus().toggleItalic().run();
          }
        }}
        className={editor.isActive("italic") ? "is-active" : ""}
      >
        <FaItalic />
      </button>
      <button
        onClick={() => {
          const selectedAnchor: HTMLAnchorElement | null =
            getSelectedAnchorElement();

          if (selectedAnchor) {
            const underlinedByStyle =
              selectedAnchor.style.textDecoration === "underline";

            let underlinedByDecoration: any = selectedAnchor.parentNode;
            let isUnderlinedByDecoration =
              underlinedByDecoration.nodeName === "U"; // replace with direct parent

            if (isUnderlinedByDecoration && underlinedByStyle) {
              selectedAnchor.style.textDecoration = "none !important";
              editor.chain().focus().unsetUnderline().run();
            } else if (isUnderlinedByDecoration) {
              editor.chain().focus().unsetUnderline().run();
            } else if (underlinedByStyle) {
              selectedAnchor.style.textDecoration = "none !important";
              editor.chain().focus().unsetUnderline().run();
            } else {
              editor.chain().focus().toggleUnderline().run();
              cleanWebKitTooltips(
                selectedAnchor.getAttribute("data-tooltip-id"),
                true
              );
              return;
            }
            cleanWebKitTooltips(selectedAnchor.getAttribute("data-tooltip-id"));
          } else {
            editor.chain().focus().toggleUnderline().run();
          }
        }}
        className={editor.isActive("underline") ? "is-active" : ""}
      >
        <FaUnderline style={{ marginTop: 2 }} />
      </button>

      {currentHeading}
      {currentAlignment}

      <button
        onClick={() => {
          const selectedAnchor = getSelectedAnchorElement();
          const selectedText = getSelectedEditorText(editor);
          const range = getSelectedRange();

          if (!range) return;
          if (!selectedAnchor && !selectedText) return;

          const linkURL = selectedAnchor?.getAttribute("data-id");
          const linkLabel = selectedAnchor?.getAttribute("data-label");
          const tooltipId = selectedAnchor?.getAttribute("data-tooltip-id");

          openLinkModal(
            !!selectedAnchor,
            selectedText,
            range,
            tooltipId,
            linkURL,
            linkLabel
          );
        }}
        className={editor.isActive("underline") ? "is-active" : ""}
        id="tiptap-link"
      >
        <FaLink />
      </button>

      <button
        onClick={() => {
          editor.commands.toggleTaskList();
        }}
        className={editor.isActive("taskList") ? "is-active" : ""}
      >
        <BsCheckCircleFill style={{ marginTop: 0 }} color={"white"} />
      </button>
    </div>
  );
}

export interface TiptapMenuHandle {
  getTiptapMenu: () => Instance | null;
}

interface TiptapMenuProps {
  editor: Editor;
  tooltips?: React.MutableRefObject<{ [href: string]: Instance }>;
}

const TiptapMenu: React.ForwardRefRenderFunction<
  TiptapMenuHandle,
  TiptapMenuProps
> = (props, tiptapMenuRef) => {
  const { editor, tooltips } = props;
  if (!editor) return null;

  const tippyInstance = useRef<Instance | null>(null);
  const currentClientRef = useRef<any>(null);
  const bubbleMenuRectRef = useRef<DOMRect | null>(null);
  const isModalOpenRef = useRef<boolean>(false);
  const setShowMergeMenu = useSetRecoilState(ShowMergeMenuAtom);

  const modals = useRecoilValue(ModalAtom);
  const { isWebKit } = useBrowserAgent();

  useEffect(() => {
    // For WebKit browsers, limit the bubble menu
    // displayment for when there's no modal opened, only.
    isModalOpenRef.current = isWebKit && !!modals.length;
  }, [modals, isWebKit]);

  useImperativeHandle(
    tiptapMenuRef,
    () => ({
      getTiptapMenu: () => tippyInstance.current,
    }),
    [tippyInstance.current]
  );

  return (
    <div>
      <motion.div layoutId="BubbleMenu">
        <BubbleMenu
          className={`bubble-menu`}
          tooltips={tooltips}
          isModalOpen={isModalOpenRef}
          tippyOptions={{
            duration: 125,
            placement: "top",
            sticky: false,
            followCursor: false,
            onShow: (instance) => {
              if (instance.props.getReferenceClientRect)
                currentClientRef.current =
                  instance.props.getReferenceClientRect();
              tippyInstance.current = instance;
            },

            onHide: (instance) => {
              instance.setProps({
                placement: "top",
              });
              currentClientRef.current = null;

              setShowMergeMenu(false);
            },

            onBeforeUpdate: (instance, partialProps) => {
              tippyInstance.current = instance;
              const tippyRoot = instance.popper;
              const proseEditor = document.querySelector(
                ".ProseMirror"
              ) as HTMLDivElement | null;
              if (!tippyRoot || !proseEditor) return bubbleMenuRectRef.current;
              if (
                partialProps.getReferenceClientRect &&
                currentClientRef.current
              ) {
                partialProps.getReferenceClientRect = () => {
                  try {
                    const selectedElement =
                      window.getSelection().anchorNode.parentNode;
                    const editorElement =
                      document.querySelector(".ProseMirror");
                    if (!editorElement?.contains(selectedElement)) {
                      return bubbleMenuRectRef.current;
                    }

                    const currentRect = window
                      .getSelection()
                      .getRangeAt(0)
                      .getBoundingClientRect();
                    if (!currentRect?.x && !currentRect?.y) {
                      return bubbleMenuRectRef.current;
                    }
                    bubbleMenuRectRef.current = currentRect;
                    return currentRect;
                  } catch (error) {
                    return bubbleMenuRectRef.current;
                  }
                };
              }
            },
          }}
          editor={editor}
        >
          <BubbleButtons
            editor={editor}
            tippyInstance={tippyInstance.current}
            tooltips={tooltips}
          />
        </BubbleMenu>
      </motion.div>
    </div>
  );
};

export default React.forwardRef(TiptapMenu);
